import { useEffect, useEffectEvent } from 'react';
import type { Lens } from '@hookform/lenses';
import { useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod/v4';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { useDateAfterTodayDeterminer } from '@interxfi/design-system/components/input/date-picker-input/util';
import { useTableOfContentsContext } from '@interxfi/design-system/components/table-of-contents/table-of-contents-context';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ResponsiveValue } from '@interxfi/design-system/util/responsive';
import { IsoV1Country, type IxcRegistrationV1IndividualProfile, IxcRegistrationV1MaritalStatus } from '@interxfi/types/generated/api';
import { AppDatePickerInputWithLens } from '../../../../../components/form/app-date-picker-input';
import { AppEnumSelectInputWithLens } from '../../../../../components/form/app-enum-select-input';
import { AppSelectInputWithLens } from '../../../../../components/form/app-select-input';
import { AppTextInputWithLens } from '../../../../../components/form/app-text-input';
import { getOptionalSchema, OptionalField, type OptionalFieldWrapper } from '../../../../../components/form/optional-field';
import { buildCountryOptions } from '../../../../../util/country';
import { addDefinedValues } from '../../../../../util/has-value';
import {
  AddressForm,
  type AddressFormLabels,
  type AddressFormValues,
  addressFormSchema,
  defaultAddressFormValues,
  formValuesToAddress,
  getInitialFormValuesForAddress,
} from '../../../shared-form/address/address-form';
import {
  defaultIndividualTaxIdValues,
  formValuesToIndividualTaxIdentifier,
  getInitialFormValuesForIndividualTaxId,
  IndividualTaxIdForm,
  individualTaxIdFormSchema,
} from '../../../shared-form/individual-tax-id-form';
import {
  defaultNameFormValues,
  formValuesToRegistrationName,
  getInitialFormValuesForRegistrationName,
  NameForm,
  nameFormSchema,
} from '../../../shared-form/name-form';
import { defaultPhoneValues, formValuesToPhones, getInitialFormValuesForPhones, PhonesForm, phoneFormSchema } from '../../../shared-form/phone-form';
import {
  AffiliationsForm,
  affiliationsFormSchema,
  defaultAffiliationsFormValues,
  formValuesToAffiliations,
  getInitialFormValuesForAffiliations,
} from './affiliations/affiliations-form';
import {
  defaultEmploymentFormValues,
  EmploymentForm,
  employmentFormSchema,
  formValuesToEmployment,
  getInitialFormValuesForEmployment,
} from './employment/employment-form';
import {
  formValuesToIndividualW9,
  getInitialFormValuesForIndividualW9,
  IndividualProfileW9Form,
  type IndividualProfileW9FormValues,
  individualProfileW9FormDefaultValues,
  individualProfileW9FormSchema,
} from './individual-profile-w9-form';
import {
  defaultInvestmentProfileFormValues,
  formValuesToInvestmentProfile,
  getInitialFormValuesForInvestmentProfile,
  InvestmentProfileForm,
  type InvestmentProfileFormValues,
  investmentProfileFormSchema,
} from './investment-profile-form';
import {
  formValuesToTrustedPersons,
  getInitialFormValuesForTrustedPersons,
  TrustedPersonsForm,
  trustedPersonsFormSchema,
} from './trusted-person-form';

const countryOptions = buildCountryOptions('en-US');

export const individualProfileFormSchema = z.object({
  type: z.literal('individual'),
  value: z.object({
    name: nameFormSchema,
    email: z.email({ error: 'Email Address must be valid' }).nonempty('Email Address is required'),
    employment: employmentFormSchema,
    birthDate: z.iso.date().nonempty('Birth Date is required'),
    phone: z.array(phoneFormSchema).min(1, 'At least one phone number is required'),
    taxIdentifier: individualTaxIdFormSchema,
    countryOfCitizenship: z.enum(IsoV1Country),
    maritalStatus: z.enum(IxcRegistrationV1MaritalStatus).or(z.literal('')),
    physicalAddress: addressFormSchema,
    mailingAddress: getOptionalSchema(addressFormSchema),
    investmentProfile: getOptionalSchema(investmentProfileFormSchema),
    affiliations: affiliationsFormSchema,
    trustedPersons: trustedPersonsFormSchema,
    w9: getOptionalSchema(individualProfileW9FormSchema),
  }),
});

export type IndividualProfileFormValues = z.infer<typeof individualProfileFormSchema>;

export const defaultIndividualProfileFormValues: IndividualProfileFormValues = {
  type: 'individual',
  value: {
    name: defaultNameFormValues,
    email: '',
    birthDate: '',
    phone: [{ ...defaultPhoneValues }],
    taxIdentifier: defaultIndividualTaxIdValues,
    countryOfCitizenship: IsoV1Country.Us,
    employment: defaultEmploymentFormValues,
    mailingAddress: {
      isProviding: false,
      value: undefined,
    },
    maritalStatus: '',
    physicalAddress: defaultAddressFormValues,
    investmentProfile: {
      isProviding: false,
      value: undefined,
    },
    affiliations: defaultAffiliationsFormValues,
    trustedPersons: [],
    w9: {
      isProviding: false,
      value: undefined,
    },
  },
};

export function getInitialFormValuesForIndividualProfile(profile: IxcRegistrationV1IndividualProfile | undefined): IndividualProfileFormValues {
  return {
    ...defaultIndividualProfileFormValues,
    value: addDefinedValues<IndividualProfileFormValues['value']>(
      {
        ...defaultIndividualProfileFormValues.value,
        mailingAddress: {
          isProviding: Boolean(profile?.mailingAddress),
          value: getInitialFormValuesForAddress(profile?.mailingAddress),
        },
        investmentProfile: {
          isProviding: Boolean(profile?.investmentProfile),
          value: profile?.investmentProfile ? getInitialFormValuesForInvestmentProfile(profile.investmentProfile) : undefined,
        },
        w9: {
          isProviding: Boolean(profile?.w9),
          value: profile?.w9 ? getInitialFormValuesForIndividualW9(profile.w9) : undefined,
        },
      },
      {
        name: getInitialFormValuesForRegistrationName(profile?.name),
        email: profile?.email,
        phone: getInitialFormValuesForPhones(profile?.phone),
        physicalAddress: getInitialFormValuesForAddress(profile?.physicalAddress),
        birthDate: profile?.birthDate,
        countryOfCitizenship: profile?.countryOfCitizenship,
        taxIdentifier: getInitialFormValuesForIndividualTaxId(profile?.taxIdentifier),
        maritalStatus: profile?.maritalStatus,
        employment: getInitialFormValuesForEmployment(profile?.employment, profile?.employee),
        affiliations: getInitialFormValuesForAffiliations(profile?.affiliations, profile?.name),
        trustedPersons: getInitialFormValuesForTrustedPersons(profile?.trustedPersons),
      },
    ),
  };
}

export function formValuesToIndividualProfile(values: IndividualProfileFormValues): IxcRegistrationV1IndividualProfile {
  return addDefinedValues<IxcRegistrationV1IndividualProfile>(
    {
      email: values.value.email,
      phone: formValuesToPhones(values.value.phone),
      name: formValuesToRegistrationName(values.value.name),
      physicalAddress: formValuesToAddress(values.value.physicalAddress),
      birthDate: values.value.birthDate,
      countryOfCitizenship: values.value.countryOfCitizenship,
      taxIdentifier: formValuesToIndividualTaxIdentifier(values.value.taxIdentifier),
      employment: formValuesToEmployment(values.value.employment),
    },
    {
      maritalStatus: values.value.maritalStatus || undefined,
      mailingAddress:
        values.value.mailingAddress.isProviding && values.value.mailingAddress.value
          ? formValuesToAddress(values.value.mailingAddress.value)
          : undefined,
      affiliations: formValuesToAffiliations(values.value.affiliations, values.value.name),
      trustedPersons: formValuesToTrustedPersons(values.value.trustedPersons),
      employee: values.value.employment.employeeOfCorrespondent,
      w9: values.value.w9.isProviding && values.value.w9.value ? formValuesToIndividualW9(values.value.w9.value) : undefined,
      investmentProfile:
        values.value.investmentProfile.isProviding && values.value.investmentProfile.value
          ? formValuesToInvestmentProfile(values.value.investmentProfile.value)
          : undefined,
    },
  );
}

export enum IndividualProfileTableOfContentsItem {
  PersonalInformation = 'personalInformation',
  InvestmentProfile = 'investmentProfile',
  W9 = 'w9',
  Affiliations = 'affiliations',
  TrustedContacts = 'trustedContacts',
}

const mailingAddressLabels = {
  usAddress: {
    street1: 'Street Address or PO Box',
  },
} satisfies AddressFormLabels;

const nameColumns: ResponsiveValue<number> = {
  sm: 1,
  md: 2,
  lg: 3,
};

interface IndividualProfileFormProps {
  lens: Lens<IndividualProfileFormValues>;
  requiresSuitabilityQuestions: boolean;
}

export function IndividualProfileForm({ lens, requiresSuitabilityQuestions }: IndividualProfileFormProps) {
  const { getTabFocusInSectionProps } = useTableOfContentsContext<IndividualProfileTableOfContentsItem>();
  const birthDateAvailabilityDeterminer = useDateAfterTodayDeterminer();
  const isProvidingSuitability = useWatch({ ...lens.focus('value.investmentProfile.isProviding').interop(), defaultValue: false });
  const { setValue } = useFormContext();

  const setRequiredInvestmentProfileValue = useEffectEvent(() => {
    setValue(
      lens.focus('value.investmentProfile').interop().name,
      {
        isProviding: true,
        value: defaultInvestmentProfileFormValues,
      },
      { shouldValidate: true },
    );
  });

  useEffect(() => {
    if (!isProvidingSuitability && requiresSuitabilityQuestions) {
      setRequiredInvestmentProfileValue();
    }
  }, [isProvidingSuitability, requiresSuitabilityQuestions]);

  return (
    <VerticalStack gap={7}>
      <VerticalStack gap={7} {...getTabFocusInSectionProps(IndividualProfileTableOfContentsItem.PersonalInformation)}>
        <VerticalStack gap={4}>
          <Typography.Level3 as="h2">Legal Name</Typography.Level3>

          <NameForm columns={nameColumns} lens={lens.focus('value.name')} />
        </VerticalStack>

        <VerticalStack gap={4}>
          <AppDatePickerInputWithLens
            dateAvailabilityDeterminer={birthDateAvailabilityDeterminer}
            label="Date of Birth"
            lens={lens.focus('value.birthDate')}
            required
          />

          <AppEnumSelectInputWithLens
            enumName="IxcRegistrationV1MaritalStatus"
            enumValue={IxcRegistrationV1MaritalStatus}
            i18nNamespace="ixcRegistrationV1"
            label="Marital Status"
            lens={lens.focus('value.maritalStatus')}
          />

          <AppSelectInputWithLens label="Country of Citizenship" lens={lens.focus('value.countryOfCitizenship')} options={countryOptions} required />

          <IndividualTaxIdForm lens={lens.focus('value.taxIdentifier')} />
        </VerticalStack>

        <AppTextInputWithLens autoComplete="off" label="Email Address" lens={lens.focus('value.email')} required />

        <VerticalStack gap={4}>
          <Typography.Level3 as="h2">Phone</Typography.Level3>

          <PhonesForm labelPlaceholder="Home, Mobile..." lens={lens.focus('value.phone')} minLength={1} />
        </VerticalStack>

        <VerticalStack gap={4}>
          <Typography.Level3 as="h2">Physical Address</Typography.Level3>

          <AddressForm lens={lens.focus('value.physicalAddress')} />
        </VerticalStack>

        <VerticalStack gap={7}>
          <VerticalStack gap={4}>
            <Typography.Level3 as="h2">Mailing Address</Typography.Level3>

            <OptionalField<AddressFormValues>
              defaultValue={defaultAddressFormValues}
              isInverted
              label="Same as physical address"
              lens={lens.focus('value.mailingAddress').narrow()}
              variant="standalone"
            >
              <AddressForm labels={mailingAddressLabels} lens={lens.focus('value.mailingAddress.value').defined()} />
            </OptionalField>
          </VerticalStack>
        </VerticalStack>

        <VerticalStack gap={4}>
          <Typography.Level3 as="h2">Employment</Typography.Level3>

          <EmploymentForm lens={lens.focus('value.employment')} />
        </VerticalStack>
      </VerticalStack>

      <VerticalStack gap={4} {...getTabFocusInSectionProps(IndividualProfileTableOfContentsItem.InvestmentProfile)}>
        <Typography.Level3 as="h2">Investment Profile</Typography.Level3>

        {requiresSuitabilityQuestions ? (
          <InvestmentProfileForm lens={lens.focus('value.investmentProfile.value').defined()} />
        ) : (
          <OptionalField<InvestmentProfileFormValues>
            defaultValue={defaultInvestmentProfileFormValues}
            label="Providing Investment Profile"
            lens={lens.focus('value.investmentProfile').narrow()}
          >
            <InvestmentProfileForm lens={lens.focus('value.investmentProfile.value').defined()} />
          </OptionalField>
        )}
      </VerticalStack>

      <VerticalStack gap={4} {...getTabFocusInSectionProps(IndividualProfileTableOfContentsItem.W9)}>
        <Typography.Level3 as="h2">W-9</Typography.Level3>

        <OptionalField<IndividualProfileW9FormValues>
          defaultValue={individualProfileW9FormDefaultValues}
          hint="The primary profile on an account must provide a W-9"
          hintVariant="primary"
          label="Providing W-9?"
          lens={lens.focus('value.w9').narrow<OptionalFieldWrapper<IndividualProfileW9FormValues>>()}
        >
          <IndividualProfileW9Form lens={lens.focus('value.w9.value').defined()} />
        </OptionalField>
      </VerticalStack>

      <VerticalStack gap={7} {...getTabFocusInSectionProps(IndividualProfileTableOfContentsItem.Affiliations)}>
        <Typography.Level3 as="h2">Affiliations</Typography.Level3>

        <AffiliationsForm lens={lens.focus('value.affiliations')} />
      </VerticalStack>

      <VerticalStack gap={7} {...getTabFocusInSectionProps(IndividualProfileTableOfContentsItem.TrustedContacts)}>
        <Typography.Level3 as="h2">Trusted Contact Persons</Typography.Level3>

        <TrustedPersonsForm lens={lens.focus('value.trustedPersons')} />
      </VerticalStack>
    </VerticalStack>
  );
}
